import React, {useEffect, useState, useCallback} from 'react';
import {Box} from '@mui/material';
import PropTypes from 'prop-types';
import RichEditorResultText from './Shared/RichEditorResultText';
import RichEditor from './Shared/RichEditor';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import AdmicityAddressLookup from '../../../../../shared-components/AdmicityAddressLookup';

const Address = ({
                     index,
                     componentId,
                     value,
                     onChange,
                     descriptionHtml,
                     onDescriptionHtmlChange,
                     commitDescriptionHtml,
                     isEditMode,
                     enableValidation,
                     isFormArchived,
                     accessLevel,
                     readOnly,
                     placeholderValues,
                     error,
                     audience,
                     recipientType,
                     linkedToStudents,
                     submitMode
                 }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback(
        (nextHtml) => {
            setDraftDescriptionHtml(nextHtml);
            onDescriptionHtmlChange(nextHtml);
        },
        [onDescriptionHtmlChange]
    );

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleAddressSelect = (address) => {
        onChange({
            id: componentId,
            value: address
        });
    };

    const renderEditMode = () => (
        <Box>
            <RichEditor
                isFormLive={isFormArchived}
                accessLevel={accessLevel}
                audience={audience}
                recipientType={recipientType}
                linkedToStudents={linkedToStudents}
                placeholderValues={placeholderValues}
                currentDescription={draftDescriptionHtml}
                setCurrentDescription={handleDraftChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
            <RequiredCheckbox
                enableValidation={enableValidation}
                index={index}
                onChange={onChange}
                isFormArchived={isFormArchived}
            />
        </Box>
    );

    const renderViewMode = () => (
        <>
            <RichEditorResultText
                id={componentId}
                description={descriptionHtml}
                placeholderValues={placeholderValues}
                readOnly={readOnly}
                enableValidation={enableValidation}
                audience={audience}
                recipientType={recipientType}
                linkedToStudents={linkedToStudents}
                submitMode={submitMode}
            />
            <Box mt={2}>
                <AdmicityAddressLookup
                    onAddressSelect={handleAddressSelect}
                    readonly={readOnly}
                    initialValue={value}
                    defaultCountry={process.env.REACT_APP_DEFAULT_COUNTRY}
                    errors={error}
                    enableValidation={enableValidation}
                />
            </Box>
        </>
    );

    return <Box sx={{mt: 2}}>{isEditMode ? renderEditMode() : renderViewMode()}</Box>;
};

Address.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.object,
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    accessLevel: PropTypes.number,
    placeholderValues: PropTypes.object,
    error: PropTypes.object,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    submitMode: PropTypes.bool
};

export default Address;
